import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Leaf, Users, Shield, Award, ChevronDown, ChevronUp, Save } from "lucide-react";

const CATEGORY_ICONS = { environmental: Leaf, social: Users, governance: Shield };
const CATEGORY_COLORS = { environmental: "text-green-600", social: "text-blue-600", governance: "text-purple-600" };
const GRADE_COLORS = { "A+": "bg-emerald-500", "A": "bg-green-500", "B": "bg-teal-500", "C": "bg-amber-500", "D": "bg-orange-500", "F": "bg-red-500" };

export default function ESGManagement() {
  const [criteria, setCriteria] = useState({});
  const [venues, setVenues] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState("");
  const [metCriteria, setMetCriteria] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedCat, setExpandedCat] = useState("environmental");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [criteriaRes, venuesRes, assessRes, lbRes] = await Promise.all([
        api.get("/esg/criteria"),
        api.get("/venues"),
        api.get("/esg/all"),
        api.get("/esg/leaderboard"),
      ]);
      setCriteria(criteriaRes.data.criteria || {});
      setVenues(venuesRes.data || []);
      setAssessments(assessRes.data.assessments || []);
      setLeaderboard(lbRes.data.venues || []);
    } catch (err) {
      toast.error("Failed to load ESG data");
    } finally { setLoading(false); }
  };

  const loadVenueAssessment = async (venueId) => {
    setSelectedVenue(venueId);
    setMetCriteria([]);
    setNotes("");
    if (!venueId) return;
    try {
      const res = await api.get(`/esg/venue/${venueId}`);
      if (res.data.has_score) {
        setMetCriteria(res.data.met_criteria || []);
        setNotes(res.data.notes || "");
      }
    } catch {}
  };

  const toggleCriteria = (id) => {
    setMetCriteria(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const saveAssessment = async () => {
    if (!selectedVenue) { toast.error("Select a venue first"); return; }
    setSaving(true);
    try {
      await api.post("/esg/assess", { venue_id: selectedVenue, met_criteria: metCriteria, notes });
      toast.success("ESG assessment saved!");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="esg-management-page">
      <div className="flex items-center gap-3">
        <Leaf className="w-6 h-6 text-green-600" />
        <h1 className="font-heading text-2xl sm:text-3xl">ESG Scoring</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Leaderboard */}
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" /> ESG Leaderboard</CardTitle></CardHeader>
          <CardContent>
            {leaderboard.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No venues scored yet</p>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((v, i) => (
                  <div key={v.venue_id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-muted-foreground w-5">#{i + 1}</span>
                      <div>
                        <p className="text-sm font-medium">{v.name}</p>
                        <p className="text-xs text-muted-foreground">{v.city}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold">{v.esg_score}%</span>
                      <Badge className={`${GRADE_COLORS[v.esg_grade] || "bg-gray-500"} text-white text-xs`}>{v.esg_grade}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assessment Form */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading text-base">Assess Venue</CardTitle>
            <Select value={selectedVenue} onValueChange={loadVenueAssessment}>
              <SelectTrigger data-testid="esg-venue-select"><SelectValue placeholder="Select a venue to assess" /></SelectTrigger>
              <SelectContent>
                {venues.map(v => (
                  <SelectItem key={v.venue_id} value={v.venue_id}>{v.name} - {v.city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedVenue && Object.entries(criteria).map(([cat, items]) => {
              const Icon = CATEGORY_ICONS[cat] || Shield;
              const color = CATEGORY_COLORS[cat];
              const isExpanded = expandedCat === cat;
              const catScore = items.filter(c => metCriteria.includes(c.id)).reduce((s, c) => s + c.points, 0);
              const catMax = items.reduce((s, c) => s + c.points, 0);
              return (
                <div key={cat} className="border rounded-lg overflow-hidden">
                  <button onClick={() => setExpandedCat(isExpanded ? "" : cat)}
                    className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors" data-testid={`esg-cat-${cat}`}>
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${color}`} />
                      <span className="font-heading text-sm uppercase tracking-wide capitalize">{cat}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold">{catScore}/{catMax}</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="p-3 border-t space-y-2">
                      {items.map(c => (
                        <label key={c.id} className="flex items-center gap-3 py-1 cursor-pointer hover:bg-muted/30 px-2 rounded" data-testid={`esg-${c.id}`}>
                          <Checkbox checked={metCriteria.includes(c.id)} onCheckedChange={() => toggleCriteria(c.id)} />
                          <span className="text-sm flex-1">{c.label}</span>
                          <Badge variant="outline" className="text-xs">{c.points} pts</Badge>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {selectedVenue && (
              <>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Assessment notes..." className="text-sm" data-testid="esg-notes" />
                <Button onClick={saveAssessment} disabled={saving} className="w-full rounded-full" data-testid="save-esg-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save ESG Assessment"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
